import { useAuth } from '../contexts/AuthContext'
import { apiGet } from '../lib/api'
import { useState, useEffect } from 'react'

interface EnrollmentStatus {
  id: string
  organizationName: string
  contactName: string
  contactEmail: string
  contactPhone: string
  location: string
  facilityType: string
  status: 'pending' | 'approved' | 'rejected' | 'completed'
  reviewNotes: string | null
  reviewedAt: string | null
  createdAt: string
  updatedAt: string
}

interface UserProfile {
  id: string
  email: string
  username: string
  first_name: string
  last_name: string
  phone: string | null
  role: string
  status: string
  email_verified: boolean
  last_login: string | null
  created_at: string
  updated_at: string
}

export function Dashboard() {
  const { user } = useAuth()
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [profileLoading, setProfileLoading] = useState(true)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [enrollment, setEnrollment] = useState<EnrollmentStatus | null>(null)
  const [enrollmentLoading, setEnrollmentLoading] = useState(true)
  const [enrollmentError, setEnrollmentError] = useState<string | null>(null)

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'

  useEffect(() => {
    if (user) {
      fetchUserProfile()
      if (!isAdmin) {
        // Only fetch enrollment status for non-admin users
        fetchEnrollmentStatus()
      } else {
        // Admins don't need enrollment status
        setEnrollmentLoading(false)
      }
    }
  }, [user, isAdmin])

  const fetchUserProfile = async () => {
    try {
      setProfileLoading(true)
      setProfileError(null)
      const response = await apiGet('/auth/me')

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        setProfileError(data.error?.message || 'Failed to load profile')
        return
      }

      const data = await response.json()
      if (data.success && data.data?.user) {
        setUserProfile(data.data.user)
      }
    } catch (err) {
      console.error('Failed to fetch user profile:', err)
      setProfileError('Failed to load profile')
    } finally {
      setProfileLoading(false)
    }
  }

  const fetchEnrollmentStatus = async () => {
    try {
      setEnrollmentLoading(true)
      setEnrollmentError(null)
      const response = await apiGet('/enrollment/status')

      if (response.status === 404) {
        // No enrollment found - this is okay for regular users
        setEnrollment(null)
        setEnrollmentError(null)
        return
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        setEnrollmentError(data.error?.message || 'Failed to load enrollment status')
        return
      }

      const data = await response.json()
      if (data.success && data.data?.enrollment) {
        setEnrollment(data.data.enrollment)
      } else {
        setEnrollment(null)
      }
    } catch (err) {
      console.error('Failed to fetch enrollment status:', err)
      setEnrollmentError('Failed to load enrollment status')
    } finally {
      setEnrollmentLoading(false)
    }
  }

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      case 'completed':
        return 'bg-blue-100 text-blue-800'
      case 'pending':
      default:
        return 'bg-yellow-100 text-yellow-800'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-sm sm:text-base text-gray-600">
          Welcome back, {userProfile ? `${userProfile.first_name} ${userProfile.last_name}`.trim() || userProfile.email : user?.name || user?.email}
        </p>
      </div>

      {/* User Profile Section */}
      {!isAdmin && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Profile</h2>
          {profileLoading ? (
            <p className="text-gray-600">Loading profile...</p>
          ) : profileError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{profileError}</p>
            </div>
          ) : userProfile ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Full Name</h3>
                  <p className="text-base text-gray-900">
                    {`${userProfile.first_name} ${userProfile.last_name}`.trim() || 'Not provided'}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Email Address</h3>
                  <p className="text-base text-gray-900">{userProfile.email}</p>
                  {userProfile.email_verified ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                      ✓ Verified
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mt-1">
                      ⚠ Not Verified
                    </span>
                  )}
                </div>
                {userProfile.phone && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Phone Number</h3>
                    <p className="text-base text-gray-900">{userProfile.phone}</p>
                  </div>
                )}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Account Status</h3>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      userProfile.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {userProfile.status.charAt(0).toUpperCase() + userProfile.status.slice(1)}
                  </span>
                </div>
              </div>
              {userProfile.last_login && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    Last login: {formatDate(userProfile.last_login)}
                  </p>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Enrollment Status Section - Only for non-admin users */}
      {!isAdmin && (
        <>
          {enrollmentLoading ? (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <p className="text-gray-600">Loading enrollment status...</p>
            </div>
          ) : enrollmentError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">{enrollmentError}</p>
            </div>
          ) : enrollment ? (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Enrollment Status</h2>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(enrollment.status)}`}
            >
              {enrollment.status.charAt(0).toUpperCase() + enrollment.status.slice(1)}
            </span>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Organization</h3>
              <p className="text-base text-gray-900">{enrollment.organizationName}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Facility Type</h3>
                <p className="text-base text-gray-900">{enrollment.facilityType}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Location</h3>
                <p className="text-base text-gray-900">
                  {enrollment.location.charAt(0).toUpperCase() + enrollment.location.slice(1).replace('-', ' ')}
                </p>
              </div>
            </div>

            {enrollment.reviewNotes && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Review Notes</h3>
                <p className="text-base text-gray-900">{enrollment.reviewNotes}</p>
                {enrollment.reviewedAt && (
                  <p className="text-sm text-gray-500 mt-1">Reviewed on {formatDate(enrollment.reviewedAt)}</p>
                )}
              </div>
            )}

            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Submitted on {formatDate(enrollment.createdAt)}
              </p>
            </div>
          </div>
        </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <p className="text-gray-600">
                You don't have any enrollment applications yet. Submit an enrollment request to get started.
              </p>
            </div>
          )}
        </>
      )}

      {/* Admin Dashboard Summary */}
      {isAdmin && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Admin Dashboard</h2>
          <p className="text-gray-600">
            Use the navigation menu above to manage users and facility enrollments.
          </p>
        </div>
      )}
    </div>
  )
}

